import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useOrg } from '../../context/OrgContext';
import { notificationAPI, adminOrgAPI, authAPI, uploadAPI } from '../../services/api';
import { getIndustryConfig } from '../../config/industryConfig';
import toast from 'react-hot-toast';
import { io } from 'socket.io-client';
import { compressImage } from '../../services/compress';
import {
  HiOutlineHome, HiOutlineTruck, HiOutlineCog, HiOutlineClipboardCheck,
  HiOutlineCube, HiOutlineShoppingCart, HiOutlineChartBar, HiOutlineLightningBolt,
  HiOutlineBell, HiOutlineLogout, HiOutlineAdjustments, HiOutlineUsers,
  HiOutlineDocumentReport, HiOutlineUserGroup, HiOutlineMenu, HiOutlineOfficeBuilding,
  HiOutlineSun, HiOutlineMoon, HiOutlineClipboardList, HiOutlineDocumentText, HiOutlineDatabase,
  HiOutlineReceiptTax
} from 'react-icons/hi';

// const SOCKET_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`;
// const SOCKET_URL = process.env.REACT_APP_API_URL || `http://localhost:9898`;
const SOCKET_URL = process.env.REACT_APP_API_URL || (window.location.port ? `${window.location.protocol}//${window.location.hostname}:9898` : window.location.origin);

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/uploads/')) {
    const apiURL = process.env.REACT_APP_API_URL;
    if (apiURL) {
      const base = apiURL.endsWith('/api') ? apiURL : (apiURL.endsWith('/api/') ? apiURL.slice(0, -1) : `${apiURL}/api`);
      return `${base}${url}`;
    }
    if (window.location.port) {
      return `${window.location.protocol}//${window.location.hostname}:9898${url}`;
    } else {
      return `${window.location.origin}/api${url}`;
    }
  }
  return url;
};


// Role-based menu configuration
const getMenuForRole = (role, email, permissions = []) => {
  const allMenus = {
    dashboard: { icon: <HiOutlineHome />, label: 'Tracker Dashboard', path: '/dashboard' },
    gateGuard: { icon: <HiOutlineTruck />, label: 'Gate Operations', path: '/gate-guard' },
    productionManager: { icon: <HiOutlineCog />, label: 'Production Manager', path: '/supervisor' },

    qualityChecker: { icon: <HiOutlineClipboardCheck />, label: 'Quality Check', path: '/quality' },
    storeManager: { icon: <HiOutlineCube />, label: 'Store & Godown', path: '/store' },
    assembly: { icon: <HiOutlineCog />, label: 'Assembly Line (Step 7-9)', path: '/assembly' },
    dispatch: { icon: <HiOutlineTruck />, label: 'Dispatch & Shipping', path: '/dispatch' },
    sales: { icon: <HiOutlineShoppingCart />, label: 'Sales & Orders', path: '/sales' },
    invoiceGenerator: { icon: <HiOutlineReceiptTax />, label: 'Bill & Invoice Generator', path: '/invoice-generator' },
    finance: { icon: <HiOutlineClipboardList />, label: 'Purchase & Procurement', path: '/finance' },
    domainBuilder: { icon: <HiOutlineDatabase />, label: 'Domain Builder', path: '/domain-builder' },
    users: { icon: <HiOutlineUsers />, label: 'User Management', path: '/users' },
    organizations: { icon: <HiOutlineOfficeBuilding />, label: 'SaaS Tenants', path: '/admin/organizations' },
    reports: { icon: <HiOutlineDocumentText />, label: 'Reports & Export', path: '/reports' },
    settings: { icon: <HiOutlineAdjustments />, label: 'Settings', path: '/settings' },
    support: { icon: <HiOutlineDocumentReport />, label: 'Help & Support', path: '/admin/support' },
  };

  // Define what each role can see
  const roleMenus = {
    super_admin: [
      { section: 'Enterprise', items: ['dashboard', 'users', 'support'] },
      { section: 'Gate Log', items: ['gateGuard'] },
      { section: 'Production', items: ['productionManager'] },
      { section: 'QC Inspection', items: ['qualityChecker'] },
      { section: 'Store Operations', items: ['storeManager'] },
      { section: 'Assembly & Dispatch', items: ['assembly', 'dispatch'] },
      { section: 'Sales Log', items: ['sales', 'invoiceGenerator'] },
      { section: 'Finance', items: ['finance'] },
      { section: 'Reports & Export', items: ['reports'] },
      { section: 'Platform', items: ['domainBuilder', 'settings'] }
    ],
    admin: [
      { section: 'Enterprise', items: ['dashboard', 'users'] },
      { section: 'Gate Log', items: ['gateGuard'] },
      { section: 'Production', items: ['productionManager'] },
      { section: 'QC Inspection', items: ['qualityChecker'] },
      { section: 'Store Operations', items: ['storeManager'] },
      { section: 'Assembly & Dispatch', items: ['assembly', 'dispatch'] },
      { section: 'Sales Log', items: ['sales', 'invoiceGenerator'] },
      { section: 'Finance', items: ['finance'] },
      { section: 'Reports & Export', items: ['reports'] },
      { section: 'Platform', items: ['domainBuilder', 'settings'] }
    ],
    gate_guard: [
      { section: 'Gate Log', items: ['gateGuard'] },
      { section: 'Reports & Export', items: ['reports'] }
    ],
    supervisor: [
      { section: 'Production', items: ['productionManager'] },
      { section: 'QC Inspection', items: ['qualityChecker'] },
      { section: 'Reports & Export', items: ['reports'] }
    ],
    production_manager: [
      { section: 'Production', items: ['productionManager'] },
      { section: 'QC Inspection', items: ['qualityChecker'] },
      { section: 'Reports & Export', items: ['reports'] }
    ],
    quality_checker: [
      { section: 'QC Inspection', items: ['qualityChecker'] },
      { section: 'Reports & Export', items: ['reports'] }
    ],
    store_manager: [
      { section: 'Store Operations', items: ['storeManager'] },
      { section: 'Reports & Export', items: ['reports'] }
    ],
    assembly: [
      { section: 'Assembly Line', items: ['assembly'] },
      { section: 'Reports & Export', items: ['reports'] }
    ],
    assembly_user: [
      { section: 'Assembly Line', items: ['assembly'] },
      { section: 'Reports & Export', items: ['reports'] }
    ],
    assembly_manager: [
      { section: 'Assembly Line', items: ['assembly'] },
      { section: 'Reports & Export', items: ['reports'] }
    ],
    dispatch: [
      { section: 'Dispatch & Shipping', items: ['dispatch', 'assembly'] },
      { section: 'Reports & Export', items: ['reports'] }
    ],
    dispatch_manager: [
      { section: 'Dispatch & Shipping', items: ['dispatch', 'assembly'] },
      { section: 'Reports & Export', items: ['reports'] }
    ],
    sales: [
      { section: 'Sales Log', items: ['sales', 'invoiceGenerator'] },
      { section: 'Reports & Export', items: ['reports'] }
    ],
    purchase_manager: [
      { section: 'Purchase & Procurement', items: ['finance'] },
      { section: 'Reports & Export', items: ['reports'] }
    ],
    purchase_user: [
      { section: 'Purchase & Procurement', items: ['finance'] },
      { section: 'Reports & Export', items: ['reports'] }
    ]
  };

  let menuConfig = roleMenus[role];

  // For regular 'user' role with custom permissions:
  if (role === 'user' && permissions && permissions.length > 0) {
    const userItems = ['dashboard'];
    if (permissions.includes('gate_guard')) userItems.push('gateGuard');
    if (permissions.includes('supervisor')) userItems.push('supervisor');
    if (permissions.includes('quality_checker')) userItems.push('qualityChecker');
    if (permissions.includes('store_manager')) userItems.push('storeManager');
    if (permissions.includes('assembly') || permissions.includes('assembly_user') || permissions.includes('assembly_manager')) userItems.push('assembly');
    if (permissions.includes('dispatch')) userItems.push('dispatch');
    if (permissions.includes('sales')) userItems.push('sales');
    if (permissions.includes('purchase') || permissions.includes('purchase_manager') || permissions.includes('purchase_user') || permissions.includes('finance')) userItems.push('finance');
    menuConfig = [{ section: 'Workspace', items: userItems }];
  }

  if (!menuConfig) {
    menuConfig = [{ section: 'Main', items: ['dashboard'] }];
  }

  return menuConfig.map(section => ({
    section: section.section,
    items: section.items.map(key => allMenus[key]).filter(Boolean)
  }));
};


const ICON_MAP = {
  HiOutlineHome: <HiOutlineHome />,
  HiOutlineTruck: <HiOutlineTruck />,
  HiOutlineCog: <HiOutlineCog />,
  HiOutlineClipboardCheck: <HiOutlineClipboardCheck />,
  HiOutlineCube: <HiOutlineCube />,
  HiOutlineShoppingCart: <HiOutlineShoppingCart />,
  HiOutlineChartBar: <HiOutlineChartBar />,
  HiOutlineLightningBolt: <HiOutlineLightningBolt />,
  HiOutlineBell: <HiOutlineBell />,
  HiOutlineLogout: <HiOutlineLogout />,
  HiOutlineAdjustments: <HiOutlineAdjustments />,
  HiOutlineUsers: <HiOutlineUsers />,
  HiOutlineDocumentReport: <HiOutlineDocumentReport />,
  HiOutlineUserGroup: <HiOutlineUserGroup />,
  HiOutlineMenu: <HiOutlineMenu />,
  HiOutlineOfficeBuilding: <HiOutlineOfficeBuilding />,
  HiOutlineClipboardList: <HiOutlineClipboardList />,
  HiOutlineDocumentText: <HiOutlineDocumentText />,
  HiOutlineDatabase: <HiOutlineDatabase />
};

const ROLE_LABELS = {
  super_admin: 'Platform Admin',
  admin: 'Organisation Admin',
  gate_guard: 'Gate Guard',
  supervisor: 'Supervisor',
  production_manager: 'Production Manager',
  quality_checker: 'Quality Checker',
  store_manager: 'Store Manager',
  assembly: 'Assembly Operator',
  assembly_user: 'Assembly User',
  assembly_manager: 'Assembly Manager',
  sales: 'Sales Officer',
  purchase_manager: 'Purchase Manager',
  purchase_user: 'Purchase Officer',
  dispatch: 'Dispatch Officer',
  dispatch_manager: 'Dispatch Manager'
};

const ROLE_COLORS = {
  super_admin: '#0078d4',
  admin: '#8660a9',
  gate_guard: '#00b7c3',
  supervisor: '#107c41',
  assembly: '#f59e0b',
  assembly_user: '#f59e0b',
  assembly_manager: '#d97706',
  production_manager: '#107c41',
  quality_checker: '#c43e1c',
  store_manager: '#f2c811',
  sales: '#a855f7',
  purchase_manager: '#ec4899',
  purchase_user: '#d946ef',
  dispatch: '#0ea5e9',
  dispatch_manager: '#0284c7'
};

const DashboardLayout = ({ children, pageTitle = 'Dashboard' }) => {
  const { user, setUser, logout, isReverificationRequired, setIsReverificationRequired } = useAuth();
  const { settings } = useOrg();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [otp, setOtp] = useState('');
  const [verifying, setVerifying] = useState(false);
  
  // Notifications State
  const [notifications, setNotifications] = useState([]);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const prevNotifCount = useRef(0);
  const bellRef = useRef(null);

  // Profile State
  const [showProfilePanel, setShowProfilePanel] = useState(false);
  const profileRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploadingProfile, setUploadingProfile] = useState(false);

  const sidebarRef = useRef(null);

  // Restore sidebar scroll position on mount
  useEffect(() => {
    const savedScrollTop = sessionStorage.getItem('sidebar-scroll');
    if (savedScrollTop && sidebarRef.current) {
      sidebarRef.current.scrollTop = parseInt(savedScrollTop, 10);
    }
  }, []);

  const handleSidebarScroll = (e) => {
    sessionStorage.setItem('sidebar-scroll', e.target.scrollTop);
  };

  const handleProfileImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingProfile(true);
    try {
      const compressedFile = await compressImage(file);
      const formData = new FormData();
      formData.append('file', compressedFile);

      const uploadRes = await uploadAPI.uploadFile(formData);
      if (uploadRes.data && uploadRes.data.success) {
        const fileUrl = uploadRes.data.fileUrl;
        
        // Update user profile in backend
        const updateRes = await authAPI.updateProfile({ profileImage: fileUrl });
        if (updateRes.data && updateRes.data.success) {
          // Update user in context
          const updatedUser = { ...user, profileImage: fileUrl };
          setUser(updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
          toast.success('Profile image updated successfully!');
        }
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to upload profile image');
    } finally {
      setUploadingProfile(false);
    }
  };

  // Superadmin Org Selection State
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(localStorage.getItem('selectedOrgId') || '');

  useEffect(() => {
    const fetchOrgs = async () => {
      // Only platform admin can fetch all orgs. Regular org super_admins should NOT call this.
      const platformAdminEmail = process.env.REACT_APP_PLATFORM_ADMIN_EMAIL;
      if (user?.role === 'super_admin' && user?.email === platformAdminEmail) {
        try {
          const res = await adminOrgAPI.getAll();
          setOrgs(res.data.data || []);
        } catch (err) {
          console.error('Failed to load organizations for superadmin selector', err);
        }
      }
    };
    fetchOrgs();
  }, [user]);

  const handleOrgChange = (e) => {
    const orgId = e.target.value;
    setSelectedOrgId(orgId);
    if (orgId) {
      localStorage.setItem('selectedOrgId', orgId);
    } else {
      localStorage.removeItem('selectedOrgId');
    }
    window.location.reload();
  };

  const isPlatformPage = ['/admin/organizations', '/settings', '/dashboard'].includes(location.pathname);
  const needsOrgSelection = user?.role === 'super_admin' && !user?.organizationId && !selectedOrgId && !isPlatformPage;

  const isAdmin = ['super_admin', 'admin'].includes(user?.role);

  const playSound = () => {
    // Use Web Audio API beep for reliable cross-browser sound without external file
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime); // A5
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      osc.start();
      gain.gain.exponentialRampToValueAtTime(0.00001, ctx.currentTime + 0.3);
      osc.stop(ctx.currentTime + 0.3);
    } catch (e) { console.log(e); }
  };

  const fetchNotifications = async () => {
    try {
      // Superadmin fetches from their specific endpoint, others from normal endpoint.
      // The bell shows alerts only; the admin activity trace lives on the Notifications page.
      const alertsOnly = ['admin', 'super_admin'].includes(user?.role) ? { category: 'alerts' } : {};
      const res = user?.role === 'super_admin' 
        ? await notificationAPI.getSuperadminNotifications(1, 20, alertsOnly)
        : await notificationAPI.getNotifications(1, 20, alertsOnly);
        
      const notifs = res.data.data;
      setNotifications(notifs);
      
      const unreadCount = notifs.filter(n => !n.isRead).length;
      if (unreadCount > prevNotifCount.current) {
        playSound(); // Play sound if new unread appears
      }
      prevNotifCount.current = unreadCount;
    } catch (err) { console.log(err); }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 20000); // Poll every 20s as fallback

    let socket;
    if (user?.organizationId || user?.role === 'super_admin') {
      socket = io(SOCKET_URL, { withCredentials: true });
      
      socket.on('connect', () => {
        // organizationId may be populated; rooms are keyed by the plain id
        const orgId = user?.organizationId?._id || user?.organizationId;
        const isPlatformAdmin = user?.role === 'super_admin' && user?.email === process.env.REACT_APP_PLATFORM_ADMIN_EMAIL;
        if (isPlatformAdmin) {
          socket.emit('join_superadmin');
        } else if (orgId) {
          socket.emit('join_org', orgId);
          if (user?.role) {
            socket.emit('join_role_room', { orgId, role: user.role });
          }
          // Org owners (super_admin) receive the org's admin alerts too
          if (user?.role === 'super_admin') {
            socket.emit('join_role_room', { orgId, role: 'admin' });
          }
        }
      });

      const refresh = () => {
        fetchNotifications();
      };

      // Listen for the new universal notification event
      socket.on('new_notification', (newNotif) => {
        // Prepend new notification for immediate feedback, then fetch to sync
        setNotifications(prev => {
          // Avoid duplicates
          if (prev.find(n => n._id === newNotif._id)) return prev;
          return [newNotif, ...prev];
        });
        playSound();
        prevNotifCount.current += 1;
      });

      socket.on('build_updated', refresh);
      socket.on('inventory_updated', refresh);
      socket.on('quality_updated', refresh);
      socket.on('machine_updated', refresh);
      socket.on('gate_entry_updated', refresh);
    }

    return () => {
      clearInterval(interval);
      if (socket) socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, user?.organizationId]);

  const unreadCount = notifications.filter(n => !n.isRead).length;

  const handleMarkRead = async (id) => {
    try {
      await notificationAPI.markAsRead(id);
      fetchNotifications();
    } catch (e) { }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      fetchNotifications();
      setShowNotifPanel(false);
    } catch (e) { }
  };

  // Close panels on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (bellRef.current && !bellRef.current.contains(event.target)) {
        setShowNotifPanel(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfilePanel(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [bellRef, profileRef]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getDisplayName = (name) => {
    if (!name) return 'User';
    if (settings?.brandName) {
      return name
        .replace(/STR-DRG/gi, settings.brandName)
        .replace(/StarConnect/gi, settings.brandName)
        .replace(/TrackBells/gi, settings.brandName);
    }
    return name;
  };

  const getUserInitials = (name) => {
    const displayName = getDisplayName(name);
    if (!displayName) return 'U';
    return displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Base default operational menus to guarantee full admin sidebar functionality
  const defaultMenuItems = [
    { key: 'dashboard', label: 'Tracker Dashboard', icon: 'HiOutlineHome', path: '/dashboard', visible: true, roles: ['super_admin', 'admin'] },
    { key: 'processTracker', label: 'Process Tracker', icon: 'HiOutlineLightningBolt', path: '/process-tracker', visible: true, roles: ['super_admin', 'admin'] },
    { key: 'gateGuard', label: 'Gate entries', icon: 'HiOutlineTruck', path: '/gate-guard', visible: true, roles: ['super_admin', 'admin', 'gate_guard'] },
    { key: 'productionManager', label: 'Production Manager', icon: 'HiOutlineCog', path: '/supervisor', visible: true, roles: ['super_admin', 'admin', 'supervisor', 'production_manager'] },
    { key: 'qualityChecker', label: 'Quality Check', icon: 'HiOutlineClipboardCheck', path: '/quality', visible: true, roles: ['super_admin', 'admin', 'quality_checker', 'supervisor', 'production_manager'] },
    { key: 'storeManager', label: 'Store & Godown', icon: 'HiOutlineCube', path: '/store', visible: true, roles: ['super_admin', 'admin', 'store_manager'] },
    { key: 'dispatch', label: 'Dispatch & Shipping', icon: 'HiOutlineTruck', path: '/dispatch', visible: true, roles: ['super_admin', 'admin', 'dispatch', 'dispatch_manager'] },
    { key: 'sales', label: 'Sales & Orders', icon: 'HiOutlineShoppingCart', path: '/sales', visible: true, roles: ['super_admin', 'admin', 'sales'] },
    { key: 'purchase', label: 'Purchase & Procurement', icon: 'HiOutlineClipboardList', path: '/finance', visible: true, roles: ['super_admin', 'admin', 'purchase_manager', 'purchase_user'] },
    { key: 'reports', label: 'Analytics & Reports', icon: 'HiOutlineDocumentText', path: '/reports', visible: true, roles: ['super_admin', 'admin', 'gate_guard', 'supervisor', 'production_manager', 'quality_checker', 'store_manager', 'sales', 'purchase_manager', 'purchase_user', 'dispatch', 'user'] },
    { key: 'users', label: 'User Management', icon: 'HiOutlineUsers', path: '/users', visible: true, roles: ['super_admin', 'admin'] },
    { key: 'settings', label: 'Settings', icon: 'HiOutlineAdjustments', path: '/settings', visible: true, roles: ['super_admin', 'admin'] },
    { key: 'domainBuilder', label: 'Domain Builder', icon: 'HiOutlineDatabase', path: '/domain-builder', visible: true, roles: ['super_admin', 'admin'] },
  ];

  // Dynamically compile menu items based on organization settings database config
  let rawMenus = [...(settings?.menus || [])].filter(m => m.key !== 'orders' && m.key !== 'supervisor');

  // Always guarantee reports menu exists for ALL roles
  const reportsMenu = rawMenus.find(m => m.key === 'reports');
  const allSystemRoles = ['super_admin', 'admin', 'gate_guard', 'supervisor', 'production_manager', 'quality_checker', 'store_manager', 'sales', 'purchase_manager', 'purchase_user', 'dispatch', 'user'];
  if (!reportsMenu) {
    rawMenus.push({
      key: 'reports',
      label: 'Analytics & Reports',
      icon: 'HiOutlineDocumentText',
      path: '/reports',
      visible: true,
      roles: allSystemRoles
    });
  } else {
    reportsMenu.visible = true;
    reportsMenu.roles = Array.from(new Set([...(reportsMenu.roles || []), ...allSystemRoles]));
  }

  // Always guarantee notifications menu exists for authorized roles (excluding gate_guard)
  const notifRoles = ['super_admin', 'admin', 'supervisor', 'production_manager', 'quality_checker', 'store_manager', 'sales', 'purchase_manager', 'purchase_user', 'dispatch', 'user'];
  const notifMenu = rawMenus.find(m => m.key === 'notifications');
  if (!notifMenu) {
    rawMenus.push({
      key: 'notifications',
      label: 'Notifications',
      icon: 'HiOutlineBell',
      path: '/notifications',
      visible: true,
      roles: notifRoles
    });
  } else {
    notifMenu.visible = true;
    notifMenu.roles = Array.from(new Set([...(notifMenu.roles || []), ...notifRoles])).filter(r => r !== 'gate_guard');
  }
  
  // Guarantee all default operational menus exist in rawMenus
  defaultMenuItems.forEach(defItem => {
    const existingIndex = rawMenus.findIndex(m => m.key === defItem.key);
    if (existingIndex === -1) {
      rawMenus.push(defItem);
    } else {
      rawMenus[existingIndex] = {
        ...rawMenus[existingIndex],
        visible: rawMenus[existingIndex].visible !== false,
        roles: Array.from(new Set([...(rawMenus[existingIndex].roles || []), ...defItem.roles]))
      };
    }
  });

  // Always ensure dashboard menu is at the very top of the sidebar for super_admin and admin
  if (['super_admin', 'admin'].includes(user?.role)) {
    const dashIndex = rawMenus.findIndex(m => m.key === 'dashboard');
    if (dashIndex > -1) {
      const [dash] = rawMenus.splice(dashIndex, 1);
      rawMenus.unshift(dash);
    }
    // Process Tracker sits right below the dashboard
    const trackerIndex = rawMenus.findIndex(m => m.key === 'processTracker');
    if (trackerIndex > 1) {
      const [tracker] = rawMenus.splice(trackerIndex, 1);
      rawMenus.splice(1, 0, tracker);
    }
  }

  // Ensure productionManager is always placed directly after gateGuard (Gate Operations)
  const gateGuardIdx = rawMenus.findIndex(m => m.key === 'gateGuard');
  const pmIdx = rawMenus.findIndex(m => m.key === 'productionManager');
  if (gateGuardIdx > -1 && pmIdx > -1 && pmIdx !== gateGuardIdx + 1) {
    const [pmItem] = rawMenus.splice(pmIdx, 1);
    rawMenus.splice(gateGuardIdx + 1, 0, pmItem);
  }


  // Always ensure support menu is present in the list for platform admin
  if (user?.role === 'super_admin' && user?.email === process.env.REACT_APP_PLATFORM_ADMIN_EMAIL && !rawMenus.some(m => m.key === 'support')) {
    rawMenus.push({
      key: 'support',
      label: 'Help & Support',
      icon: 'HiOutlineDocumentReport',
      path: '/admin/support',
      visible: true,
      roles: ['super_admin']
    });
  }

  const hasOrdersAccess = ['super_admin', 'admin', 'sales'].includes(user?.role) || (user?.role === 'user' && user?.permissions?.includes('orders'));
  const hasFinanceAccess = ['super_admin', 'admin', 'purchase_manager', 'purchase_user', 'sales'].includes(user?.role) || (user?.role === 'user' && (user?.permissions?.includes('finance') || user?.permissions?.includes('purchase') || user?.permissions?.includes('purchase_user') || user?.permissions?.includes('purchase_manager')));
  const hasInvoiceAccess = ['super_admin', 'admin', 'sales'].includes(user?.role) || (user?.role === 'user' && (user?.permissions?.includes('finance') || user?.permissions?.includes('store')));

  // Inject Finance & Purchase menu dynamically if not present for authorized roles
  if (hasFinanceAccess) {
    const finMenu = rawMenus.find(m => m.key === 'finance' || m.key === 'purchase');
    if (finMenu) {
      if (finMenu.roles) {
        ['purchase_manager', 'purchase_user', 'user'].forEach(r => {
          if (!finMenu.roles.includes(r)) finMenu.roles.push(r);
        });
      }
    } else {
      rawMenus.push({
        key: 'purchase',
        label: 'Purchase & Procurement',
        icon: 'HiOutlineClipboardList',
        path: '/finance',
        visible: true,
        roles: ['super_admin', 'admin', 'purchase_manager', 'purchase_user', 'user']
      });
    }
  }

  // Inject Invoice Generator menu dynamically if not present for authorized roles
  if (hasInvoiceAccess) {
    const genMenu = rawMenus.find(m => m.key === 'invoiceGenerator');
    if (genMenu) {
      if (genMenu.roles) {
        ['sales', 'user'].forEach(r => {
          if (!genMenu.roles.includes(r)) genMenu.roles.push(r);
        });
      }
    } else {
      rawMenus.push({
        key: 'invoiceGenerator',
        label: 'Invoice Generator',
        icon: 'HiOutlineDocumentText',
        path: '/invoice-generator',
        visible: true,
        roles: ['super_admin', 'admin', 'sales', 'user']
      });
    }
  }

  // Module key → sidebar item key mapping
  const MODULE_TO_MENU_KEY = {
    gate: 'gateInward',
    production: 'productionManager',
    quality: 'qualityChecker',
    store: 'storeManager',
    dispatch: 'dispatch',
    sales: 'sales',
    finance: 'finance',
    purchase: 'purchase',
    reports: 'reports',
    hr: 'users',
  };

  // Determine enabledModules from org (from login context) or industryConfig
  const orgEnabledModules = user?.organizationId?.enabledModules;
  const orgSubIndustryId = user?.organizationId?.subIndustryId || 'drag_chain';
  const industryEnabledModules = getIndustryConfig(orgSubIndustryId).enabledModules;
  // Use org-specific overrides if set, otherwise fall back to industry defaults
  const effectiveEnabledModules = (orgEnabledModules && orgEnabledModules.length > 0)
    ? orgEnabledModules
    : industryEnabledModules;

  // Inject Drag Chain-specific menu items dynamically
  const isDragChainSidebar = orgSubIndustryId === 'drag_chain';
  if (isDragChainSidebar && !rawMenus.find(m => m.key === 'universalModelInfo')) {
    rawMenus.push({
      key: 'universalModelInfo',
      label: 'Universal Model Info',
      icon: 'HiOutlineDatabase',
      path: '/universal-model-info',
      visible: true,
      roles: ['admin']
    });
  }

  const activeMenus = rawMenus
    .filter(item => {
      // 1. Must be marked visible globally
      if (item.visible === false) return false;
      
      // Platform-level menus only visible to platform admin
      if (['organizations', 'support'].includes(item.key)) {
        return user?.role === 'super_admin' && user?.email === process.env.REACT_APP_PLATFORM_ADMIN_EMAIL;
      }

      // Hard safeguard: Gate operations is never visible to quality checkers
      if (['gateGuard', 'gateInward', 'gateOutward'].includes(item.key) && user?.role === 'quality_checker') return false;

      // Hard safeguard: Notifications is never visible to gate guards
      if (item.key === 'notifications' && user?.role === 'gate_guard') return false;

      // Hard safeguard: Dispatch and Purchase/Finance menus are never visible to store managers
      if (['dispatch', 'purchase', 'finance', 'invoiceGenerator'].includes(item.key) && ['store_manager', 'store'].includes(user?.role)) return false;

      // MODULE TOGGLING: Hide sidebar items for modules not enabled for this org's industry
      // Always show core items (dashboard, users, settings, organizations, support, notifications, invoiceGenerator, purchase) regardless
      const coreItems = ['dashboard', 'users', 'settings', 'organizations', 'support', 'invoiceGenerator', 'notifications', 'purchase', 'finance'];
      if (!coreItems.includes(item.key)) {
        // Find which module key this sidebar item corresponds to
        const matchedModule = Object.entries(MODULE_TO_MENU_KEY).find(([modKey, menuKey]) => 
          menuKey === item.key || 
          (modKey === 'gate' && ['gateGuard', 'gateInward', 'gateOutward'].includes(item.key)) ||
          (modKey === 'finance' && ['finance', 'purchase', 'invoiceGenerator'].includes(item.key)) ||
          (modKey === 'dispatch' && ['dispatch'].includes(item.key)) ||
          (modKey === 'sales' && ['sales', 'orders'].includes(item.key)) ||
          (modKey === 'production' && ['productionManager', 'supervisor'].includes(item.key))
        )?.[0];
        if (matchedModule && !effectiveEnabledModules.includes(matchedModule)) {
          return false; // This module is disabled for this organization's industry
        }
      }
      // Drag Chain-only menu items: show only for drag chain
      if (item.key === 'universalModelInfo') {
        return isDragChainSidebar && user?.role === 'admin';
      }

      // 2. Role access check
      if (item.roles && item.roles.length > 0) {
        // Platform admin and Organisation admin get access to everything
        if (['super_admin', 'admin'].includes(user?.role)) return true;
        
        // Custom permissions check for 'user' role
        if (user?.role === 'user' && user?.permissions) {
          const permMap = {
            gateGuard: 'gate_guard',
            gateInward: 'gate_guard',
            gateOutward: 'gate_guard',
            supervisor: 'supervisor',
            qualityChecker: 'quality_checker',
            qcInventoryRequests: 'quality_checker',
            storeManager: 'store_manager',
            dispatch: 'dispatch',
            sales: 'sales',
            orders: 'sales',
            finance: 'finance',
            purchase: 'purchase',
            invoiceGenerator: 'finance',
          };
          const reqPerm = permMap[item.key];
          if (reqPerm) {
            if (reqPerm === 'finance' || reqPerm === 'purchase') {
              return user.permissions.includes('finance') || user.permissions.includes('purchase') || user.permissions.includes('purchase_manager') || user.permissions.includes('purchase_user');
            }
            return user.permissions.includes(reqPerm);
          }
        }

        return item.roles.includes(user?.role);
      }
      return true;
    });

  // Group menus by section dynamically
  const menuItems = [];
  activeMenus.forEach(item => {
    let sectionName = item.section;
    if (!sectionName) {
      if (['dashboard', 'users', 'notifications'].includes(item.key)) sectionName = 'Enterprise';
      else if (['gateGuard', 'gateInward', 'gateOutward'].includes(item.key)) sectionName = 'Gate Log';
      else if (['supervisor', 'productionManager'].includes(item.key)) sectionName = 'Production';
      else if (['qualityChecker', 'qcInventoryRequests'].includes(item.key)) sectionName = 'QC Inspection';
      else if (item.key === 'storeManager') sectionName = 'Store Operations';
      else if (item.key === 'dispatch') sectionName = 'Dispatch & Shipping';
      else if (['sales', 'orders'].includes(item.key)) sectionName = 'Sales Log';
      else if (['purchase', 'finance', 'invoiceGenerator'].includes(item.key)) sectionName = 'Purchase & Procurement';
      else if (['organizations', 'settings'].includes(item.key)) sectionName = 'Platform';
      else if (item.key === 'universalModelInfo') sectionName = 'Drag Chain Tools';
      else sectionName = 'General';
    }

    let sect = menuItems.find(s => s.section === sectionName);
    if (!sect) {
      sect = { section: sectionName, items: [] };
      menuItems.push(sect);
    }
    
    // Resolve icon component from string name
    const iconComponent = ICON_MAP[item.icon] || <HiOutlineHome />;
    
    sect.items.push({
      ...item,
      badge: item.key === 'notifications' && unreadCount > 0 ? `${unreadCount}` : item.badge,
      icon: iconComponent
    });
  });

  // Explicit Section Ordering: Place Gate Log above General
  const SECTION_ORDER = [
    'Enterprise',
    'Gate Log',
    'Production',
    'QC Inspection',
    'Store Operations',
    'Dispatch & Shipping',
    'Sales Log',
    'Purchase & Procurement',
    'Finance',
    'Platform',
    'Drag Chain Tools',
    'General'
  ];

  menuItems.sort((a, b) => {
    const idxA = SECTION_ORDER.indexOf(a.section);
    const idxB = SECTION_ORDER.indexOf(b.section);
    const orderA = idxA === -1 ? 999 : idxA;
    const orderB = idxB === -1 ? 999 : idxB;
    return orderA - orderB;
  });

  let roleLabel = ROLE_LABELS[user?.role] || 'User';
  if (user?.role === 'super_admin') {
    roleLabel = user.email === process.env.REACT_APP_PLATFORM_ADMIN_EMAIL ? 'Platform Admin' : 'Organisation Admin';
  }
  const roleColor = ROLE_COLORS[user?.role] || '#8892b0';

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setVerifying(true);
    try {
      await adminOrgAPI.reverifyOTP({ otp });
      toast.success('Reverification successful!');
      setIsReverificationRequired(false);
      window.location.reload(); // Refresh to clear state and fetch data
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setVerifying(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      await adminOrgAPI.resendReverifyOTP();
      toast.success('OTP resent to your email');
    } catch (err) {
      toast.error('Failed to resend OTP');
    }
  };

  if (isReverificationRequired) {
    return (
      <div style={{ height: '100vh', width: '100vw', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)' }}>
        <div className="glass-card" style={{ maxWidth: '450px', width: '100%', padding: '40px', textAlign: 'center', border: '1px solid var(--danger)' }}>
          <div style={{ fontSize: '3rem', color: 'var(--danger)', marginBottom: '16px' }}>🔒</div>
          <h2 style={{ color: 'var(--text-primary)', marginBottom: '16px' }}>Reverification Required</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '24px', lineHeight: '1.5' }}>
            Your organization's access has been temporarily suspended pending verification.
            Please click "Send OTP" to email your Organization Admin, then enter the code to unlock your workspace.
          </p>

          <form onSubmit={handleVerifyOTP}>
            <input 
              type="text" 
              className="form-input" 
              placeholder="Enter 6-digit OTP" 
              value={otp} 
              onChange={e => setOtp(e.target.value)}
              maxLength={6}
              required
              style={{ textAlign: 'center', fontSize: '1.2rem', letterSpacing: '4px', marginBottom: '16px' }}
            />
            <button type="submit" className="btn btn-primary" style={{ width: '100%', marginBottom: '12px' }} disabled={verifying}>
              {verifying ? 'Verifying...' : 'Verify OTP & Unlock'}
            </button>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={handleResendOTP}>
                Send OTP
              </button>
              <button type="button" className="btn btn-secondary" style={{ flex: 1, color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={handleLogout}>
                Log Out
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-layout" style={{
      backgroundImage: 'var(--auth-bg-gradient), url("/factory_bg.png")',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      backgroundRepeat: 'no-repeat'
    }}>
      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div className="mobile-overlay" onClick={() => setIsMobileMenuOpen(false)}></div>
      )}

      {/* SIDEBAR */}
      <aside 
        ref={sidebarRef} 
        onScroll={handleSidebarScroll} 
        className={`sidebar ${isMobileMenuOpen ? 'open' : ''}`}
      >
        <div 
          className="sidebar-header" 
          onClick={() => navigate('/dashboard')}
          style={{
            cursor: 'pointer',
            ...(settings.logo?.includes('logo.png') ? { padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '1px solid var(--border)' } : {})
          }}
          title="Click to go to Dashboard"
        >
          {settings.logo && settings.logo.includes('logo.png') ? (
            <>
              <div className="logo" style={{ background: 'none', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 0, flexShrink: 0 }}>
                <img src="/logo_icon.png" alt="TrackBells Icon" style={{ maxHeight: '48px', maxWidth: '48px', objectFit: 'contain' }} />
              </div>
              <div className="brand" style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <h3 style={{ fontFamily: 'Poppins, sans-serif', fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0, lineHeight: 1.2 }}>
                  {settings.brandName || 'TrackBells'}
                </h3>
                <span style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0, lineHeight: 1.2 }}>
                  {settings.brandSubtitle || 'ERP Automation'}
                </span>
              </div>
            </>
          ) : (
            <>
              {settings.logo && (settings.logo.startsWith('http') || settings.logo.startsWith('/') || settings.logo.startsWith('data:') || settings.logo.startsWith('blob:')) ? (
                <div className="logo" style={{ background: '#ffffff', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img src={getImageUrl(settings.logo)} alt="Logo" style={{ maxHeight: '32px', maxWidth: '32px', borderRadius: '4px', objectFit: 'contain' }} />
                </div>
              ) : (
                <div className="logo" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: '1.8rem' }}>{settings.logo || '🏭'}</span>
                </div>
              )}
              <div className="brand">
                <h3>{settings.brandName || 'TrackBells ERP'}</h3>
                <span>{settings.brandSubtitle || 'Factory Automation'}</span>
              </div>
            </>
          )}
        </div>

        {/* Role Badge */}
          <div style={{
            margin: '12px 16px', padding: '8px 14px',
            background: `${roleColor}12`, border: `1px solid ${roleColor}30`,
            borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '8px'
          }}>
            <div style={{
              width: '8px', height: '8px', borderRadius: '50%',
              background: roleColor, boxShadow: `0 0 8px ${roleColor}60`
            }} />
            <span style={{ fontSize: '0.72rem', fontWeight: 600, color: roleColor, letterSpacing: '0.5px' }}>
              {roleLabel}
            </span>
          </div>

        <nav className="sidebar-nav">
          {menuItems.map((section, si) => (
            <div className="sidebar-section" key={si}>
              <div className="sidebar-section-title">{section.section}</div>
              {section.items.map((item, ii) => (
                <div
                  key={ii}
                  className={`sidebar-item ${location.pathname === item.path || (item.key === 'gateGuard' && (location.pathname === '/gate-guard' || location.pathname === '/gate-inward' || location.pathname === '/gate-outward')) ? 'active' : ''} ${item.disabled ? 'disabled' : ''}`}
                  onClick={() => {
                    if (!item.disabled) {
                      if (sidebarRef.current) {
                        sessionStorage.setItem('sidebar-scroll', sidebarRef.current.scrollTop);
                      }
                      navigate(item.path);
                      setIsMobileMenuOpen(false);
                    }
                  }}
                >
                  <span className="icon">{item.icon}</span>
                  <span>{item.label}</span>
                  {item.badge && <span className="badge soon">{item.badge}</span>}
                </div>
              ))}
            </div>
          ))}
          <div className="sidebar-section" style={{ marginTop: 'auto', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
            <div className="sidebar-item" onClick={handleLogout} style={{ color: 'var(--danger)' }}>
              <span className="icon"><HiOutlineLogout /></span>
              <span>Logout</span>
            </div>
          </div>
        </nav>
      </aside>

      {/* MAIN */}
      <div className="main-content">
        <header className="top-navbar">
          <div className="navbar-left" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button 
              className="mobile-menu-btn navbar-icon-btn" 
              onClick={() => setIsMobileMenuOpen(true)}
              style={{ display: 'none', marginRight: '4px' }}
            >
              <HiOutlineMenu />
            </button>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: settings?.logo && (settings.logo.startsWith('http') || settings.logo.startsWith('/') || settings.logo.startsWith('data:') || settings.logo.startsWith('blob:')) && !settings.logo.includes('logo.png') ? '#ffffff' : 'linear-gradient(135deg, var(--primary), var(--primary-light))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              overflow: 'hidden',
              padding: settings?.logo && (settings.logo.startsWith('http') || settings.logo.startsWith('/') || settings.logo.startsWith('data:') || settings.logo.startsWith('blob:')) && !settings.logo.includes('logo.png') ? '4px' : '0'
            }}>
              {settings?.logo && (settings.logo.startsWith('http') || settings.logo.startsWith('/') || settings.logo.startsWith('data:') || settings.logo.startsWith('blob:')) ? (
                <img src={getImageUrl(settings.logo)} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              ) : (
                <span style={{ fontSize: '20px' }}>{settings?.logo || '🏭'}</span>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <h1 className="brand-name-text" style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1.2 }}>
                {settings?.brandName || 'TrackBells ERP'}
              </h1>
              <span style={{ fontSize: '0.78rem', color: 'var(--primary)', fontWeight: 600, lineHeight: 1.2, marginTop: '2px' }}>
                {pageTitle}
              </span>
            </div>
          </div>
          <div className="navbar-right">
            {user?.role === 'super_admin' && user?.email === process.env.REACT_APP_PLATFORM_ADMIN_EMAIL && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '12px' }}>
                <span className="hide-on-mobile" style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-muted)' }}>Choose Org:</span>
                <select
                  value={selectedOrgId}
                  onChange={handleOrgChange}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                    background: 'var(--bg-input)',
                    color: 'var(--text-primary)',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">-- Select Organization --</option>
                  {orgs.map(org => (
                    <option key={org._id} value={org._id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {/* Profile Avatar / User Menu */}
            
            <div className="profile-wrapper" ref={profileRef} style={{ position: 'relative' }}>
              <div className="user-menu" onClick={() => setShowProfilePanel(!showProfilePanel)} title="View Profile">
                <div className="user-avatar" style={{ overflow: 'hidden' }}>
                  {user?.profileImage ? (
                    <img src={getImageUrl(user.profileImage)} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    getUserInitials(user?.name)
                  )}
                </div>
                <div className="user-info">
                  <div className="user-name">{getDisplayName(user?.name)}</div>
                  <div className="user-role" style={{ color: roleColor }}>{roleLabel}</div>
                </div>
              </div>

              {/* PROFILE PANEL */}
              {showProfilePanel && (
                <div className="notification-dropdown glass-card" style={{ width: '300px' }}>
                  <div className="notif-header" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '24px 16px' }}>
                    <div className="user-avatar" style={{ width: '64px', height: '64px', fontSize: '1.5rem', background: `linear-gradient(135deg, ${roleColor}, var(--accent))`, overflow: 'hidden', position: 'relative' }}>
                      {user?.profileImage ? (
                        <img src={getImageUrl(user.profileImage)} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        getUserInitials(user?.name)
                      )}
                    </div>
                    
                    {/* Upload button */}
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                      <input 
                        type="file" 
                        ref={fileInputRef} 
                        onChange={handleProfileImageChange} 
                        accept="image/*" 
                        style={{ display: 'none' }} 
                      />
                      <button 
                        onClick={() => fileInputRef.current?.click()} 
                        disabled={uploadingProfile}
                        style={{
                          background: 'rgba(255, 255, 255, 0.08)',
                          border: '1px solid var(--border)',
                          borderRadius: '6px',
                          padding: '4px 12px',
                          color: 'var(--text-primary)',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          fontWeight: 600,
                          transition: 'background 0.2s'
                        }}
                        onMouseOver={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'}
                        onMouseOut={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)'}
                      >
                        {uploadingProfile ? 'Uploading...' : 'Change Photo'}
                      </button>
                    </div>

                    <div style={{ textAlign: 'center' }}>
                      <h4 style={{ margin: 0, fontSize: '1.2rem', marginBottom: '4px' }}>{getDisplayName(user?.name)}</h4>
                      <span style={{ fontSize: '0.8rem', color: roleColor, fontWeight: 600, background: `${roleColor}15`, padding: '4px 12px', borderRadius: '12px' }}>
                        {roleLabel}
                      </span>
                    </div>
                  </div>
                  <div className="notif-body" style={{ padding: '16px' }}>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Email</label>
                      <div style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>{user?.email}</div>
                    </div>
                    {user?.phone && (
                      <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Mobile Number</label>
                        <div style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>{user?.phone}</div>
                      </div>
                    )}
                    
                    {/* Organization Field logic - Non-editable, hiding for platform super_admin if no org */}
                    {!['super_admin', 'admin'].includes(user?.role) && user?.organizationId && user?.organizationId?.name ? (
                      <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Organization</label>
                        <div style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>{user.organizationId.name}</div>
                      </div>
                    ) : null}
                  </div>
                  <div style={{ borderTop: '1px solid var(--border)', padding: '12px' }}>
                    <button className="btn btn-secondary" style={{ width: '100%', display: 'flex', justifyContent: 'center', color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={handleLogout}>
                      <HiOutlineLogout /> Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>
        <div className="dashboard-content">
          {needsOrgSelection ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 24px',
              textAlign: 'center',
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: '16px',
              maxWidth: '600px',
              margin: '60px auto',
              boxShadow: 'var(--shadow-md)'
            }}>
              <div style={{ fontSize: '3.5rem', marginBottom: '20px' }}>🏢</div>
              <h3 style={{ color: 'var(--text-primary)', fontSize: '1.4rem', fontWeight: 700, marginBottom: '12px' }}>
                Select an Organization to Manage
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '24px', maxWidth: '480px' }}>
                Aap platform Super Admin hain. Is page ke features (Add, Edit, Delete aur logs) ko view/manage karne ke liye kripya top navbar me se kisi ek <b>Organization</b> ko select karein.
              </p>
              {orgs.length > 0 && (
                <div style={{ width: '100%', maxWidth: '280px' }}>
                  <select
                    value={selectedOrgId}
                    onChange={handleOrgChange}
                    className="form-input"
                    style={{ textAlign: 'center', fontWeight: 600, cursor: 'pointer' }}
                  >
                    <option value="">-- Choose Organization --</option>
                    {orgs.map(org => (
                      <option key={org._id} value={org._id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          ) : (
            <>
              {children}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
